// Platform-agnostic storage wrapper
// Uses localStorage on web, AsyncStorage on native

import {Platform} from 'react-native';

interface Storage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

const webStorage: Storage = {
  async getItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  },
  async setItem(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  },
  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  },
};

// Eagerly require native modules (only runs on native due to Platform.OS gate below)
const AsyncStorage = Platform.OS !== 'web'
  ? require('@react-native-async-storage/async-storage').default
  : null;

// General-purpose storage (AsyncStorage on native, localStorage on web)
export const storage: Storage = Platform.OS === 'web'
  ? webStorage
  : {
      async getItem(key: string): Promise<string | null> {
        return AsyncStorage!.getItem(key);
      },
      async setItem(key: string, value: string): Promise<void> {
        return AsyncStorage!.setItem(key, value);
      },
      async removeItem(key: string): Promise<void> {
        return AsyncStorage!.removeItem(key);
      },
    };

// Secure storage for sensitive data (session tokens)
// Uses EncryptedSharedPreferences on Android, Keychain on iOS, localStorage on web
const EncryptedStorage = Platform.OS !== 'web'
  ? require('react-native-encrypted-storage').default
  : null;

export const secureStorage: Storage = Platform.OS === 'web'
  ? webStorage
  : {
      async getItem(key: string): Promise<string | null> {
        return EncryptedStorage!.getItem(key);
      },
      async setItem(key: string, value: string): Promise<void> {
        await EncryptedStorage!.setItem(key, value);
      },
      async removeItem(key: string): Promise<void> {
        await EncryptedStorage!.removeItem(key);
      },
    };
